import React, { useMemo, useEffect, useRef, useState } from 'react';
import { TIER } from '../utils/tier-constants';
import { getMaxZipCodes, hasAdvancedFilters } from '../utils/tier-utils';
import { PLANS } from '../config/plans';
import { BulkImportResult } from './ZoneSearch';
import { ValidationIssue } from '../utils/subscriptionValidation';
import { WhereGroup } from './subscription/WhereGroup';
import { WhatGroup } from './subscription/WhatGroup';
import { FeaturesGroup } from './subscription/FeaturesGroup';
import { ChannelsGroup } from './subscription/ChannelsGroup';
// Minimal utility to extract all ZIPs from a node tree
import {
    extractZips,
    shouldEatZone,
    isZoneBlocked,
    consolidateZones,
    isPolygonNeighbour,
    isPolygonConnected,
    getPolygonSelectionExtent,
    MAX_SELECTION_EXTENT_KM,
    ZoneNode,
    splitZone,
} from '../utils/zoneUtils';
import { useGeoData } from '../contexts/GeoDataContext';

import { useAuth } from '../contexts/AuthContext';

export interface SubscriptionFilters {
  minRent: string;
  maxRent: string;
  minBaseRent: string;
  maxBaseRent: string;
  includeUnknowns: boolean;
  minRooms: string;
  maxRooms: string;
  minSpace: string;
  maxSpace: string;
  wbsFilter: 'ANY' | 'ONLY' | 'NONE';
  wgFilter: 'ANY' | 'ONLY' | 'NONE';
  swapFilter: 'ANY' | 'ONLY' | 'NONE';
  tempFilter: 'ANY' | 'ONLY' | 'NONE';
  providerFilter: 'ANY' | 'COMMERCIAL' | 'PRIVATE';
  furnishedFilter: 'ANY' | 'ONLY' | 'NONE';
  closedAreaFilter: 'ANY' | 'ONLY' | 'NONE';
}

export interface SubscriptionFormData {
  zipCodes: string[];
  notificationChannels: string[];
  enabled: boolean;
  filters: SubscriptionFilters;
  uiMetadata?: {
    zones: ZoneNode[];
    /** UI-only label for the search; not (yet) part of the backend schema. */
    name?: string;
  };
}

interface Props {
  formData: SubscriptionFormData;
  userTier: string;
  isLoading: boolean;
  onFieldChange: (field: string | Partial<SubscriptionFormData>, value?: any) => void;
  onFilterChange: (field: string, value: string) => void;
  isAdmin?: boolean;
  issues?: ValidationIssue[];
  isBaseRentActive?: boolean;
  onBaseRentToggle?: (active: boolean) => void;
  /** 'page': grouped Suchdialog (T7a). 'telegram': the compact Mini-App layout (T7b). */
  variant?: 'page' | 'telegram';
  onConstraintChange?: (hasConstraint: boolean) => void;
  otherSubscriptionZips?: Set<string>;
}


// Helper to normalize legacy nodes (recursive)
const normalizeNode = (node: any): ZoneNode => {
    // Map old property names to new compressed ones if they exist
    const typeMap: Record<string, string> = {
        'Zip': 'ZI', 'City': 'CI', 'District': 'DI', 'Region': 'RE', 'State': 'ST', 'Country': 'CO', 'Town': 'TO'
    };
    const rawType = node.t || node.type;

    return {
        i: node.i || node.id,
        t: typeMap[rawType] || rawType,
        l: node.l || node.label,
        c: (node.c || node.children)?.map(normalizeNode)
    };
};

export const SubscriptionEditorFields: React.FC<Props> = ({
  formData,
  userTier,
  isLoading,
  onFieldChange,
  onFilterChange,
  isAdmin = false,
  issues = [],
  isBaseRentActive = false,
  onBaseRentToggle,
  variant = 'page',
  onConstraintChange,
  otherSubscriptionZips,
}) => {
  const { user } = useAuth();
  const zones = useMemo<ZoneNode[]>(() => {
    // 1. Get base zones from uiMetadata or legacy zipCodes
    let baseZones: ZoneNode[] = [];
    if (formData.uiMetadata?.zones && formData.uiMetadata.zones.length > 0) {
      baseZones = formData.uiMetadata.zones.map(normalizeNode);
    } else if (formData.zipCodes && formData.zipCodes.length > 0) {
      baseZones = formData.zipCodes.map(zip => ({
        i: `legacy_zip_${zip}`,
        t: 'ZI',
        l: zip,
      }));
    }

    // 2. Auto-Reconciliation: Check if any ZIP codes from the root list are missing in the zones
    if (formData.zipCodes && formData.zipCodes.length > 0) {
      const existingZips = new Set(baseZones.flatMap(extractZips));
      const missingZips = formData.zipCodes.filter(zip => !existingZips.has(zip));

      if (missingZips.length > 0) {
        // Add missing ZIPs as legacy nodes
        const reconciledZones = [
          ...baseZones,
          ...missingZips.map(zip => ({
             i: `reconciled_zip_${zip}`,
             t: 'ZI' as const,
             l: zip,
          }))
        ];
        return reconciledZones;
      }
    }

    return baseZones;
  }, [formData.uiMetadata, formData.zipCodes]);

  const allSelectedZips = useMemo(() => {
      return Array.from(new Set(zones.flatMap(extractZips)));
  }, [zones]);

  // Local override for Kernzone ZIP ordering (FREE tier).
  // Stores the user-chosen zip order without restructuring zone tree.
  // Reset when allSelectedZips changes (ZIP added/removed).
  const [kernZipOrder, setKernZipOrder] = useState<string[] | null>(null);
  const prevAllZipsRef = React.useRef<string>(allSelectedZips.join(','));
  if (prevAllZipsRef.current !== allSelectedZips.join(',')) {
      prevAllZipsRef.current = allSelectedZips.join(',');
      setKernZipOrder(null);
  }

  // Effective zip list for KernzonenField: use kernZipOrder override if set
  const effectiveZipOrder = useMemo(() => {
      if (!kernZipOrder) return allSelectedZips;
      const kernSet = new Set(kernZipOrder.slice(0, 3));
      const rest = allSelectedZips.filter(z => !kernSet.has(z));
      return [...kernZipOrder.slice(0, 3), ...rest];
  }, [kernZipOrder, allSelectedZips]);

  const isPremium = hasAdvancedFilters(userTier, isAdmin);
  const maxZipCodes = getMaxZipCodes(userTier, isAdmin);
  const showKernzonen = userTier === TIER.FREE && allSelectedZips.length > 3;
  const showFreeHint = !isAdmin && userTier === TIER.FREE && !PLANS.free.filtersApplied;

  // -- HANDLERS --

  // Update parent state (both uiMetadata and zipCodes) atomically
  const updateZones = React.useCallback((newZones: ZoneNode[]) => {
      const allZips = Array.from(new Set(newZones.flatMap(extractZips)));

      // Use the new batch update support
      onFieldChange({
          uiMetadata: { ...(formData.uiMetadata || { zones: [] }), zones: newZones },
          zipCodes: allZips
      });
  }, [onFieldChange, formData.uiMetadata]);

  // -- CONSTRAINT MESSAGE STATE --
  const [constraintMessage, setConstraintMessage] = useState<string | null>(null);

  // Notify parent when constraint state changes (for save button blocking)
  useEffect(() => {
      onConstraintChange?.(constraintMessage !== null);
  }, [constraintMessage, onConstraintChange]);

  // -- CONSOLIDATION ON LOAD --
  const { zoneDefinitions, loadZones, isLoadingZones, polygons, loadPolygons } = useGeoData();
  const hasConsolidatedRef = useRef(false);

  useEffect(() => {
      loadZones();
  }, [loadZones]);

  useEffect(() => {
      loadPolygons();
  }, [loadPolygons]);

  useEffect(() => {
    // Only run if both subscription data and definitions are ready, and we haven't consolidated yet
    if (isLoading || isLoadingZones || !zoneDefinitions || hasConsolidatedRef.current) return;

    // If no zones selected, we are done with initial consolidation
    if (zones.length === 0) {
        hasConsolidatedRef.current = true;
        return;
    }

    const consolidated = consolidateZones(zones, zoneDefinitions);

    // Check if actual changes happened (content-based) to avoid unnecessary update
    const currentIds = new Set(zones.map(z => z.i));
    const newIds = new Set(consolidated.map(z => z.i));

    let changed = currentIds.size !== newIds.size;
    if (!changed) {
        for (const z of consolidated) {
            if (!currentIds.has(z.i)) {
                changed = true;
                break;
            }
        }
    }

    // Set ref to true BEFORE triggering state update to ensure parallel effects don't fire again
    hasConsolidatedRef.current = true;

    if (changed) {
        updateZones(consolidated);
    }

  }, [isLoading, isLoadingZones, zones, zoneDefinitions, updateZones]);

  const handleZoneSelect = (node: ZoneNode) => {
      // 1. Tier check (free user limit)
      if (!isAdmin && userTier === TIER.FREE && allSelectedZips.length >= maxZipCodes) {
          return; // Limits handled by parent/UI disable
      }
      // 2. Duplicate check
      if (zones.some(z => z.i === node.i)) {
          setConstraintMessage(`${node.l || node.i} ist bereits in dieser Suche enthalten.`);
          return;
      }

      // 3. Eating/blocking logic
      const blockingZone = zones.find(z => isZoneBlocked(node, z));
      if (blockingZone) {
          setConstraintMessage(
              `${node.l || node.i} ist bereits über ${blockingZone.l || blockingZone.i} in dieser Suche enthalten.`
          );
          return;
      }

      const filteredZones = zones.filter(z => !shouldEatZone(node, z));
      const newZones = [...filteredZones, node];

      // 4. ZIP count cap — applies to all tiers including admin
      const newZipCount = Array.from(new Set(newZones.flatMap(extractZips))).length;
      if (newZipCount > maxZipCodes) {
          const current = allSelectedZips.length;
          const added = Array.from(new Set(extractZips(node))).length;
          setConstraintMessage(
              `Diese Zone würde die Grenze von ${maxZipCodes} PLZ überschreiten` +
              ` (aktuell ${current}, +${added}).`
          );
          return;
      }

      // 5. Neighbour, radius, and connectivity checks — only when there is
      //    already a selection. Polygon data used for geometric precision.
      //    All checks are permissive (allow) when polygon data is not yet loaded.
      if (zones.length >= 1) {
          const candidateZips = Array.from(new Set(extractZips(node)));
          const existingZips = allSelectedZips;

          // 5a. Neighbour check (polygon intersection)
          if (!isPolygonNeighbour(candidateZips, existingZips, polygons)) {
              setConstraintMessage(
                  'Diese Zone liegt zu weit entfernt. Bitte wähle nur angrenzende Gebiete.'
              );
              return;
          }

          // 5b. Extent check (prevents snake selections)
          const newZipList = Array.from(new Set(newZones.flatMap(extractZips)));
          const extent = getPolygonSelectionExtent(newZipList, polygons);
          if (extent > MAX_SELECTION_EXTENT_KM) {
              setConstraintMessage(
                  `Die Auswahl ist zu groß. Die maximale Ausdehnung beträgt ${MAX_SELECTION_EXTENT_KM} km` +
                  ` (aktuell ca. ${Math.round(extent)} km).`
              );
              return;
          }

          // 5c. Connectivity check (no snake / island shapes)
          if (!isPolygonConnected(newZipList, polygons)) {
              setConstraintMessage(
                  'Diese Zone würde die Auswahl in getrennte Teile aufteilen. Nur zusammenhängende Gebiete sind erlaubt.'
              );
              return;
          }
      }

      // All checks passed
      setConstraintMessage(null);
      updateZones(newZones);
  };

  const handleBulkZoneSelect = (nodes: ZoneNode[]): BulkImportResult => {
      const result: BulkImportResult = { added: 0, notFound: [], duplicates: 0 };
      let currentZones = [...zones];

      for (const node of nodes) {
          // Duplicate check
          if (currentZones.some(z => z.i === node.i)) {
              result.duplicates++;
              continue;
          }

          // Blocking check (skip blocked nodes silently)
          if (currentZones.some(z => isZoneBlocked(node, z))) {
              result.duplicates++;
              continue;
          }

          // Eating logic
          const filteredZones = currentZones.filter(z => !shouldEatZone(node, z));
          const candidateZones = [...filteredZones, node];

          // ZIP count cap
          const newZipCount = Array.from(new Set(candidateZones.flatMap(extractZips))).length;
          if (newZipCount > maxZipCodes) {
              // Stop adding — we've hit the limit
              setConstraintMessage(
                  `PLZ-Limit erreicht (${maxZipCodes}). ${nodes.length - result.added - result.duplicates} PLZ konnten nicht hinzugefügt werden.`
              );
              break;
          }

          currentZones = candidateZones;
          result.added++;
      }

      if (result.added > 0) {
          updateZones(currentZones);

          // Post-import connectivity check — blocks saving if zones are not connected
          const allZips = Array.from(new Set(currentZones.flatMap(extractZips)));
          if (allZips.length >= 2 && !isPolygonConnected(allZips, polygons)) {
              setConstraintMessage(
                  'Die eingefügten PLZ bilden kein zusammenhängendes Gebiet. Bitte entferne nicht verbundene PLZ, um speichern zu können.'
              );
          } else {
              setConstraintMessage(null);
          }
      }

      return result;
  };

  const handleResetAll = () => {
      if (window.confirm('Möchtest du wirklich alle ausgewählten Gebiete entfernen?')) {
          updateZones([]);
      }
  };

  const handleZoneRemove = (zoneId: string) => {
      const newZones = zones.filter(z => z.i !== zoneId);
      setConstraintMessage(null);
      updateZones(newZones);
  };

  const handleZoneExplode = (zoneId: string) => {
      const target = zones.find(z => z.i === zoneId);
      if (!target || !target.c || target.c.length === 0) return;
      // Replace parent with children
      const others = zones.filter(z => z.i !== zoneId);

      // Filter out duplicate children (if already present in 'others', though unlikely)
      const newChildren = splitZone(target).children.filter(child => !others.some(o => o.i === child.i));
      const newZones = [...others, ...newChildren];

      // ZIP cap safety check (neighbour/radius intentionally skipped — geographic
      // footprint is unchanged; only the node hierarchy is restructured).
      const newZipCount = Array.from(new Set(newZones.flatMap(extractZips))).length;
      if (newZipCount > maxZipCodes) {
          setConstraintMessage(
              `Verfeinern würde die Grenze von ${maxZipCodes} PLZ überschreiten.`
          );
          return;
      }

      setConstraintMessage(null);
      updateZones(newZones);
  };

  const handleZipAdd = (zip: string) => {
      handleZoneSelect({ i: zip, l: zip, t: 'ZI' });
  };

  const handleZipRemove = (zip: string) => {
      const processNode = (node: ZoneNode): ZoneNode[] => {
          // If it's a Zip node, check if it's the one to remove
          if (node.t === 'ZI' && (node.l || node.i) === zip) return [];
          // If it's a manual zone with zipCodes
          const nodeAny = node as any;
          if (nodeAny.zipCodes && nodeAny.zipCodes.includes(zip)) {
              const remaining = nodeAny.zipCodes.filter((z: string) => z !== zip);
              if (remaining.length === 0) return [];
              return [{ ...node, zipCodes: remaining } as ZoneNode];
          }
          // If it's a parent node containing the zip
          if (node.c && node.c.length > 0) {
              const containsZip = node.c.some(child => extractZips(child).includes(zip));
              if (containsZip) {
                  // Explode: replace this parent with its children, recursing on them
                  return node.c.flatMap(processNode);
              }
          }
          return [node];
      };

      // Deduplicate zones by ID to prevent duplicates after explosions
      const deduplicatedZones = Array.from(new Map(zones.flatMap(processNode).map(z => [z.i, z])).values());
      setConstraintMessage(null);
      updateZones(deduplicatedZones);
  };

  const handleNameChange = (value: string) => {
      onFieldChange({ uiMetadata: { ...(formData.uiMetadata || { zones: [] }), name: value } });
  };

  const hasRegisteredDevices = user?.devices && Object.keys(user.devices).length > 0;
  const pushPermissionDenied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
  const isEmailVerified = !!user?.emailVerified;
  const currentChannel = formData.notificationChannels[0]?.toLowerCase() || 'none';
  const showPushWarning = currentChannel === 'push' && !hasRegisteredDevices;

  const handleChannelChange = (value: string) => {
      onFieldChange('notificationChannels', value === 'none' ? [] : [value]);
  };

  if (variant === 'page') {
    return (
      <div className="subscription-editor-fields">
        <WhereGroup
          name={formData.uiMetadata?.name || ''}
          onNameChange={handleNameChange}
          zones={zones}
          allSelectedZips={allSelectedZips}
          maxZipCodes={maxZipCodes}
          zoneSearchDisabled={!isAdmin && userTier === TIER.FREE && allSelectedZips.length >= maxZipCodes}
          isLoading={isLoading}
          onSelectZone={handleZoneSelect}
          onBulkSelectZones={handleBulkZoneSelect}
          otherSubscriptionZips={otherSubscriptionZips}
          constraintMessage={constraintMessage}
          onZoneRemove={handleZoneRemove}
          onZoneExplode={handleZoneExplode}
          onResetAll={handleResetAll}
          showKernzonen={showKernzonen}
          effectiveZipOrder={effectiveZipOrder}
          onKernChange={(newZips) => {
              setKernZipOrder(newZips.slice(0, 3));
              onFieldChange({ zipCodes: newZips });
          }}
          onAddZip={handleZipAdd}
          onRemoveZip={handleZipRemove}
        />

        <WhatGroup
          filters={formData.filters}
          issues={issues}
          isLoading={isLoading}
          onFilterChange={onFilterChange}
          isBaseRentActive={isBaseRentActive}
          onBaseRentToggle={(active) => onBaseRentToggle?.(active)}
          showFreeHint={showFreeHint}
        />

        <FeaturesGroup
          filters={formData.filters}
          onFilterChange={onFilterChange}
          isLoading={isLoading}
          showFreeHint={showFreeHint}
        />

        <ChannelsGroup
          currentChannel={currentChannel}
          onChange={handleChannelChange}
          isLoading={isLoading}
          isPremium={isPremium}
          hasEmail={!!user?.email}
          isEmailVerified={isEmailVerified}
          pushPermissionDenied={pushPermissionDenied}
          showPushWarning={showPushWarning}
        />
      </div>
    );
  }

  // --- Telegram Mini App layout: same field groups as the browser Suchdialog, at a
  //     compact density (native-style muted uppercase section titles instead of Section's
  //     icon+h2, tighter gaps). Channel choice and the push toggle are hidden — Telegram
  //     users get Meldungen in the chat by definition — and the map starts collapsed. ---
  return (
    <div className="subscription-editor-fields flex flex-col gap-3" data-density="compact">
      <WhereGroup
        density="compact"
        name={formData.uiMetadata?.name || ''}
        onNameChange={handleNameChange}
        zones={zones}
        allSelectedZips={allSelectedZips}
        maxZipCodes={maxZipCodes}
        zoneSearchDisabled={!isAdmin && userTier === TIER.FREE && allSelectedZips.length >= maxZipCodes}
        isLoading={isLoading}
        onSelectZone={handleZoneSelect}
        onBulkSelectZones={handleBulkZoneSelect}
        otherSubscriptionZips={otherSubscriptionZips}
        constraintMessage={constraintMessage}
        onZoneRemove={handleZoneRemove}
        onZoneExplode={handleZoneExplode}
        onResetAll={handleResetAll}
        showKernzonen={showKernzonen}
        effectiveZipOrder={effectiveZipOrder}
        onKernChange={(newZips) => {
            setKernZipOrder(newZips.slice(0, 3));
            onFieldChange({ zipCodes: newZips });
        }}
        onAddZip={handleZipAdd}
        onRemoveZip={handleZipRemove}
      />

      <WhatGroup
        density="compact"
        filters={formData.filters}
        issues={issues}
        isLoading={isLoading}
        onFilterChange={onFilterChange}
        isBaseRentActive={isBaseRentActive}
        onBaseRentToggle={(active) => onBaseRentToggle?.(active)}
        showFreeHint={showFreeHint}
      />

      <FeaturesGroup
        density="compact"
        filters={formData.filters}
        onFilterChange={onFilterChange}
        isLoading={isLoading}
        showFreeHint={showFreeHint}
      />
    </div>
  );
};
